<template>
  <el-dialog
    :visible.sync="visible"
    title="选择门店"
    width="1080px"
    :before-close="handleClose"
    :append-to-body="true"
    :destroy-on-close="true"
    @open="handleOpen"
  >
    <div class="dialog-content">
      <el-alert :title="alertText" type="info" :closable="false"> </el-alert>

      <SelectionTable
        v-if="visible"
        ref="selectionTableRef"
        v-model="selectedStores"
        :columns="columns"
        :data="tableData"
        :loading="loading"
        :pagination="pagination"
        :max-count="20"
        @change="handleTableChange"
      >
        <template #search>
          <div class="search-area">
            <div class="search-form">
              <div class="form-item">
                <span class="label">门店名称</span>
                <el-input
                  v-model="storeName"
                  filterable
                  clearable
                  v-deleteEmoji
                  placeholder="请填写"
                />
                <el-button type="primary" class="search-btn" @click="handleSearch"
                  >查询</el-button
                >
              </div>
            </div>
          </div>
        </template>
        <template #extra>
          <div class="add-store">
            <span class="text">没有适合的门店，</span>
            <el-button type="text" class="action" @click="handleAddStore"
              >点击新建</el-button
            >
          </div>
        </template>
      </SelectionTable>
    </div>

    <template #footer>
      <div class="footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button
          type="primary"
          @click="handleConfirm"
          :disabled="selectedStores.length === 0"
          >确定</el-button
        >
      </div>
    </template>
  </el-dialog>
</template>

<script setup>
import { storeList } from '@/api/domain/shop'
import SelectionTable from '@/components/SelectionTable'
import { use$to } from '@/utils/useUtils'
import WindowCommunication from '@/utils/windowCommunication'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useEditFormState } from '../../hooks/useEditFormState'

const { state: formState } = useEditFormState()

const $to = use$to()
const emits = defineEmits(['confirm'])

const selectionTableRef = ref(null)
const visible = ref(false)
const loading = ref(false)
const selectedStores = ref([])
const tableData = ref([])
const storeName = ref('')

const pagination = ref({
  pageNum: 1,
  pageSize: 10,
  total: 0,
})

const alertText = computed(() => {
  return `请选择【${
    formState.value.cityName || '--'
  }】下可交付提车的门店，最多可选20家。`
})

// 表格列配置
const columns = [
  {
    name: 'id',
    columnProps: {
      prop: 'id',
      label: '门店ID',
    },
  },
  {
    name: 'name',
    columnProps: {
      prop: 'name',
      label: '门店名称',
    },
  },
  {
    name: 'address',
    columnProps: {
      prop: 'address',
      label: '门店地址',
    },
  },
  {
    name: 'contactTel',
    columnProps: {
      prop: 'contactTel',
      label: '联系方式',
    },
  },
]

const handleTableChange = ({ pagination: pager }) => {
  pagination.value.pageSize = pager.pageSize
  pagination.value.pageNum = pager.pageNum
  getTableData()
}

const handleSearch = () => {
  pagination.value.pageNum = 1
  getTableData()
}

const getTableData = async () => {
  loading.value = true
  const [err, res] = await $to(
    storeList({
      pageNum: pagination.value.pageNum,
      pageSize: pagination.value.pageSize,
      name: storeName.value,
      cityCode: formState.value.cityCode,
    }),
  )
  loading.value = false
  if (err || res?.code !== 1) return
  const { items = [], totalNum = 0 } = res.data
  tableData.value = items
  pagination.value = {
    ...pagination.value,
    total: totalNum,
  }
}

const handleClose = () => {
  storeName.value = ''
  selectedStores.value = []
  tableData.value = []
  visible.value = false
}

const handleConfirm = () => {
  if (!selectedStores.value.length) return
  emits('confirm', selectedStores.value)
  handleClose()
}

const handleOpen = async () => {
  await getTableData()
}

const handleAddStore = () => {
  const url = '/companyNew/company/storeManage/add?scene=1'
  const newWindow = window.open(url, '_blank')
  const currentWindow = window

  if (newWindow) {
    newWindow.addEventListener('beforeunload', () => {
      setTimeout(() => {
        currentWindow.focus()
      }, 100)
    })
  }
}

defineExpose({
  open: (initialSelectedStores = []) => {
    selectedStores.value = initialSelectedStores
    visible.value = true
  },
  close: () => {
    visible.value = false
  },
})

// 新建窗口通信
const communication = ref(null)
const handleStoreAdded = (data) => {
  if (data) {
    loading.value = true
    setTimeout(() => {
      pagination.value.pageNum = 1
      getTableData()
    }, 2000)
  }
}

onMounted(() => {
  communication.value = new WindowCommunication('lease_store_added', {
    timeout: 2000,
  })
  communication.value.on('lease_store_added', handleStoreAdded)
})

onUnmounted(() => {
  if (communication.value) {
    communication.value.off('lease_store_added', handleStoreAdded)
    communication.value.destroy()
  }
})
</script>

<style lang="scss" scoped>
::v-deep {
  .el-alert__content {
    height: 22px;
  }
}
::v-deep .el-alert {
  padding: 7px 16px;
  margin-bottom: 12px;
}
.search-btn {
  margin-left: 12px;
}
.footer {
  margin-top: 20px;
  text-align: right;
}
.search-area {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  .search-form {
    .form-item {
      display: flex;
      align-items: center;

      .label {
        padding: 0 12px;
        height: 32px;
        width: 120px;
        line-height: 32px;
        border-radius: 6px 0 0 6px;
        background: #ffffff;
        border-width: 1px 0 1px 1px;
        border-style: solid;
        border-color: #e7e8eb;
        box-sizing: border-box;
        opacity: 1;
      }
    }
  }
  .add-store {
    display: flex;
    align-items: center;
    font-size: 14px;

    .text {
      color: rgba(0, 0, 0, 0.6);
    }

    .action {
      padding: 0;
      margin-left: 4px;
      font-size: 14px;
      color: #366cfe;

      &:hover {
        color: #366cfe;
      }
    }
  }
}

::v-deep {
  .el-input {
    .el-input__inner {
      border-radius: 0 6px 6px 0;
      height: 32px;
      line-height: 32px;
      background: #ffffff;
      border: 1px solid #e7e8eb;
      box-sizing: border-box;
      opacity: 1;
    }
    .el-input__inner:hover,
    .el-input__inner:focus {
      border-color: #e7e8eb;
    }
  }
}
</style>
